import React, { useMemo, useState } from "react";
import InputField from "components/atoms/InputField/InputField";
import { useForm } from "react-hook-form";
import Button from "components/atoms/Button/Button";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { toast } from "react-toastify";
import { updateUserPassword } from "store/slices/user.slice";
import { Card } from "components/atoms/Card";
import { RequirmentCaracter } from "components/atoms/RequirmentCaracter";
import Heading from "components/atoms/layout/Heading/Heading";

export const PasswordChange: React.FC = () => {
  const dispatch = useAppDispatch();
  const [passwordFocus, setPasswordFocus] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    mode: "onChange",
  });
  const isDisabled = useMemo(() => {
    return watch('newPassword') === watch('confirmPassword') && watch('currentPassword') && !!watch('newPassword') && !!watch('confirmPassword')
  }, [watch()])
  const save = (data: any) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error("Please check confirmed password");
    } else {
      dispatch(
        updateUserPassword({
          newPassword: data.newPassword,
          currentPassword: data.currentPassword,
        })
      );
    }
  };
  const checkPasswordRules = useMemo(() => ({
    hasUpperAndLower: /(?=.*?[A-Z])(?=.*?[a-z])/.test(watch('newPassword')),
    hasNumber: /(?=.*?[0-9])/.test(watch('newPassword')),
    hasSpecialChar: /(?=.*?[#?!@$ %^&*-])/.test(watch('newPassword')),
    hasMinLength: watch('newPassword')?.length >= 8,
    hasEqual: watch('newPassword') === watch('confirmPassword'),
  }), [watch('newPassword'), watch('confirmPassword')]);
  return (
    <Card title="Change Password" icon="lock" subtitle="Update your password to keep your account secure">
      <form onSubmit={handleSubmit(save)} className="flex flex-col gap-4">
        <InputField
          label="Current Password"
          type="password"
          name="currentPassword"
          errors={errors}
          register={register}
          validationSchema={{
            required: "Current Password is required",
          }}
        />
        <InputField
          label="New Password"
          type="password"
          name="newPassword"
          errors={errors}
          register={register}
          onFocus={() => setPasswordFocus(true)}
          validationSchema={{
            required: "New Password is required",
            pattern: {
              value:
                /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$ %^&*-]).{8,}$/,
              message:
                "Minimum eight characters, at least one upper case English letter, one lower case English letter, one number and one special character",
            },
          }}
        />
        <InputField
          label="Confirm Password"
          type="password"
          name="confirmPassword"
          errors={errors}
          register={register}
          onBlur={() => setPasswordFocus(false)}
          validationSchema={{
            required: "Confirm Password is required",
            validate: (value: string) => value === watch('newPassword') || "Passwords do not match"
          }}
        />
        <ul className={`transition-all flex flex-col list-none duration-500 overflow-hidden ${passwordFocus ? 'max-h-96 opacity-100' : 'max-h-0 '
          }`}>
          <RequirmentCaracter label="8 characters" isChecked={checkPasswordRules.hasMinLength} />
          <RequirmentCaracter label="1 uppercase letter and 1 lowercase letter" isChecked={checkPasswordRules.hasUpperAndLower} />
          <RequirmentCaracter label="1 number" isChecked={checkPasswordRules.hasNumber} />
          <RequirmentCaracter label="1 special character" isChecked={checkPasswordRules.hasSpecialChar} />
          <RequirmentCaracter label="New password need match with confirm password" isChecked={checkPasswordRules.hasEqual} />
        </ul>
        <div className="flex justify-end">
          <Button disabled={!isDisabled}>Update Password</Button>
        </div>
      </form>
    </Card>
  );
};